import React, { useState, useEffect, useRef } from 'react';
import { Avatar, StatusBadge, ScoreBar } from '../components/UI';
import { STATUS_CONFIG } from '../components/UI';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';

function formatPhone(val) {
  const d = val.replace(/[^0-9]/g, '').replace(/^1/, '').slice(0, 10);
  if (d.length === 0) return '';
  if (d.length <= 3) return '+1 (' + d;
  if (d.length <= 6) return '+1 (' + d.slice(0,3) + ') ' + d.slice(3);
  return '+1 (' + d.slice(0,3) + ') ' + d.slice(3,6) + '-' + d.slice(6);
}

export default function Leads() {
  const { user } = useAuth();
  const toast = useToast();
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [newLead, setNewLead] = useState({ name: "", phone: "", coverage: "", email: "" });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [massMsg, setMassMsg] = useState("");
  const [selectedWorkflow, setSelectedWorkflow] = useState("");
  const [workflows, setWorkflows] = useState([]);
  const [automationMode, setAutomationMode] = useState(user?.automation_mode || 'autonomous');
  const fileInputRef = useRef(null);

  const loadLeads = () => api.getLeads({ status: filter }).then(d => { setLeads(d); setLoading(false); }).catch(() => setLoading(false));

  useEffect(() => {
    loadLeads();
    api.me().then(u => setAutomationMode(u.automation_mode || 'autonomous')).catch(() => {});
    api.getWorkflows().then(setWorkflows).catch(() => {});
  }, [filter]);

  const handleAddLead = async () => {
    if (!newLead.name || !newLead.phone) return;
    try { await api.createLead(newLead); await loadLeads(); setShowAdd(false); setNewLead({ name: "", phone: "", coverage: "", email: "" }); }
    catch (err) { alert(err.message); }
  };

  const handleAiToggle = async (lead, enable) => {
    try {
      await api.toggleLeadAI(lead._id || lead.id, { ai_enabled: enable });
      await loadLeads();
    } catch (err) { alert(err.message); }
  };

  const handleMassText = async () => {
    if (!massMsg.trim() || selected.length === 0) return;
    try {
      await api.massText({ lead_ids: selected, message: massMsg });
      setMassMsg(""); setSelected([]); setSelectedWorkflow("");
      toast({ message: `Sent to ${selected.length} lead${selected.length > 1 ? 's' : ''}.` });
    } catch (err) { alert(err.message); }
  };

  const handleMassDelete = async () => {
    if (!window.confirm(`Delete ${selected.length} lead${selected.length > 1 ? 's' : ''}? This cannot be undone.`)) return;
    try {
      await Promise.all(selected.map(id => api.deleteLead(id)));
      setSelected([]);
      await loadLeads();
    } catch (err) { alert(err.message); }
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const formData = new FormData(); formData.append('file', file);
    try {
      const data = await api.uploadLeads(formData);
      await loadLeads();
      toast({ message: `Uploaded ${data.added} lead${data.added !== 1 ? 's' : ''}${data.skipped > 0 ? `, ${data.skipped} skipped` : ''}.` });
    } catch (err) { toast({ message: 'Upload failed: ' + err.message, type: 'error' }); }
    e.target.value = '';
  };

  const toggleSelect = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const toggleAll = () => setSelected(s => s.length === leads.length ? [] : leads.map(l => l._id || l.id));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0, height: "calc(100vh - 92px)" }}>
      <div style={{ background: "var(--bg2)", borderRadius: 16, border: "0.5px solid rgba(83,74,183,0.3)", overflow: "hidden", boxShadow: "0 0 30px rgba(83,74,183,0.08)", display: "flex", flexDirection: "column", flex: 1 }}>

        {/* Header */}
        <div style={{ padding: "14px 20px", borderBottom: "0.5px solid var(--border)", background: "linear-gradient(135deg,rgba(83,74,183,0.08),rgba(29,158,117,0.06))", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", flexShrink: 0 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#534AB7", boxShadow: "0 0 8px #534AB7", flexShrink: 0 }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>Leads <span style={{ color: "var(--text3)", fontWeight: 400 }}>({leads.length})</span></span>

          <select value={filter} onChange={e => setFilter(e.target.value)} style={{ padding: "5px 10px", borderRadius: 8, border: "0.5px solid var(--border2)", background: "var(--bg3)", fontSize: 12, color: "var(--text)", cursor: "pointer" }}>
            <option value="all">All statuses</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>

          {selected.length > 0 && (
            <>
              <select value={selectedWorkflow} onChange={e => { setSelectedWorkflow(e.target.value); if (e.target.value) { const w = workflows.find(x => (x._id || x.id) === e.target.value); if (w && w.steps && w.steps[0]) setMassMsg(w.steps[0].message); } }} style={{ padding: "5px 10px", borderRadius: 8, border: "0.5px solid var(--border2)", background: "var(--bg3)", fontSize: 12, color: "var(--text)", cursor: "pointer", maxWidth: 160 }}>
                <option value="">Select workflow...</option>
                {workflows.map(w => <option key={w._id || w.id} value={w._id || w.id}>{w.name}</option>)}
              </select>
              <input value={massMsg} onChange={e => { setMassMsg(e.target.value); setSelectedWorkflow(""); }} placeholder={"Or type message to " + selected.length + " leads..."} style={{ flex: 1, minWidth: 140, padding: "5px 10px", borderRadius: 8, border: "0.5px solid var(--border2)", background: "var(--bg3)", fontSize: 12, color: "var(--text)" }} />
              <button onClick={handleMassText} style={{ padding: "6px 14px", borderRadius: 8, border: "none", background: "linear-gradient(135deg,#534AB7,#1D9E75)", color: "#fff", fontSize: 12, cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap", boxShadow: "0 2px 8px rgba(83,74,183,0.3)" }}>Send to {selected.length}</button>
              <button onClick={handleMassDelete} style={{ padding: "6px 12px", borderRadius: 8, border: "0.5px solid rgba(240,149,149,0.5)", background: "transparent", color: "#A32D2D", fontSize: 12, cursor: "pointer", fontWeight: 500, whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 4 }}>
                <i className="ti ti-trash" style={{ fontSize: 13 }} aria-hidden="true" /> Delete {selected.length}
              </button>
            </>
          )}

          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <input type="file" ref={fileInputRef} accept=".csv" onChange={handleCSVUpload} style={{ display: "none" }} />
            <button onClick={() => fileInputRef.current && fileInputRef.current.click()} style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 8, border: "0.5px solid var(--border2)", background: "var(--bg3)", fontSize: 12, cursor: "pointer", color: "var(--text2)", fontWeight: 500 }}>
              <i className="ti ti-upload" style={{ fontSize: 13 }} aria-hidden="true" /> CSV
            </button>
            <button onClick={() => setShowAdd(s => !s)} style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 14px", borderRadius: 8, border: "none", background: "linear-gradient(135deg,#534AB7,#1D9E75)", fontSize: 12, cursor: "pointer", color: "#fff", fontWeight: 600, boxShadow: "0 2px 8px rgba(83,74,183,0.3)" }}>
              <i className="ti ti-plus" style={{ fontSize: 13 }} aria-hidden="true" /> Add lead
            </button>
          </div>
        </div>

        {/* Add lead form */}
        {showAdd && (
          <div style={{ padding: "14px 20px", borderBottom: "0.5px solid var(--border)", background: "var(--bg3)", display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr auto", gap: 10, alignItems: "end", flexShrink: 0 }}>
            {[{ k: "name", placeholder: "Full name *" }, { k: "phone", placeholder: "Phone *" }, { k: "coverage", placeholder: "Coverage type" }, { k: "email", placeholder: "Email (optional)" }].map(f => (
              <input key={f.k} value={newLead[f.k]} onChange={e => setNewLead(n => ({ ...n, [f.k]: f.k === 'phone' ? formatPhone(e.target.value) : e.target.value }))} placeholder={f.placeholder} style={{ padding: "8px 12px", borderRadius: 8, border: "0.5px solid var(--border2)", background: "var(--bg2)", fontSize: 12, color: "var(--text)" }} />
            ))}
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={handleAddLead} style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: "linear-gradient(135deg,#534AB7,#1D9E75)", color: "#fff", fontSize: 12, cursor: "pointer", fontWeight: 600 }}>Add</button>
              <button onClick={() => setShowAdd(false)} style={{ padding: "8px 12px", borderRadius: 8, border: "0.5px solid var(--border2)", background: "transparent", fontSize: 12, cursor: "pointer", color: "var(--text)" }}>Cancel</button>
            </div>
          </div>
        )}

        {/* Table */}
        <div style={{ overflow: "auto", flex: 1 }}>
          {loading && <div style={{ padding: 40, textAlign: "center", color: "var(--text3)", fontSize: 13 }}>Loading...</div>}
          {!loading && leads.length === 0 && (
            <div style={{ padding: "60px 20px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
              <div style={{ width: 64, height: 64, borderRadius: 20, background: "linear-gradient(135deg,rgba(83,74,183,0.08),rgba(29,158,117,0.08))", border: "0.5px solid rgba(83,74,183,0.18)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className="ti ti-users" style={{ fontSize: 28, color: "var(--text3)" }} aria-hidden="true" />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)", marginBottom: 6 }}>No leads yet</div>
                <div style={{ fontSize: 13, color: "var(--text3)", maxWidth: 260, lineHeight: 1.5 }}>Add your first lead manually, upload a CSV, or run the Lead Scraper to get started.</div>
              </div>
              <button onClick={() => setShowAdd(true)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 18px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#534AB7,#1D9E75)", fontSize: 13, cursor: "pointer", color: "#fff", fontWeight: 600, boxShadow: "0 2px 8px rgba(83,74,183,0.3)" }}>
                <i className="ti ti-plus" style={{ fontSize: 14 }} aria-hidden="true" /> Add first lead
              </button>
            </div>
          )}
          {!loading && leads.length > 0 && (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "var(--bg3)", position: "sticky", top: 0 }}>
                  <th style={{ padding: "10px 16px" }}><input type="checkbox" checked={selected.length === leads.length && leads.length > 0} onChange={toggleAll} /></th>
                  {["Name", "Coverage", "Status", "Score", "Added", "AI"].map(h => (
                    <th key={h} style={{ padding: "10px 16px", fontSize: 11, fontWeight: 600, color: "var(--text2)", textAlign: "left", textTransform: "uppercase", letterSpacing: "0.5px" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {leads.map(l => (
                  <tr key={l._id || l.id} style={{ borderBottom: "0.5px solid var(--border)", transition: "background 0.1s" }}
                    onMouseEnter={e => e.currentTarget.style.background = "var(--bg3)"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <td style={{ padding: "10px 16px" }} onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={selected.includes(l._id || l.id)} onChange={() => toggleSelect(l._id || l.id)} />
                    </td>
                    <td style={{ padding: "10px 16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <Avatar name={l.name} size={30} />
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13, color: "var(--text)" }}>{l.name}</div>
                          <div style={{ fontSize: 11, color: "var(--text2)" }}>{l.phone}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: "10px 16px", color: "var(--text2)", fontSize: 12 }}>{l.coverage || "—"}</td>
                    <td style={{ padding: "10px 16px" }}><StatusBadge status={l.status} /></td>
                    <td style={{ padding: "10px 16px" }}><ScoreBar score={l.score || 0} /></td>
                    <td style={{ padding: "10px 16px", color: "var(--text2)", fontSize: 12 }}>{l.created_at ? new Date(l.created_at).toLocaleDateString() : "—"}</td>
                    <td style={{ padding: "10px 16px" }} onClick={e => e.stopPropagation()}>
                      {l.ai_enabled === true
                        ? <button onClick={() => handleAiToggle(l, false)} style={{ padding: "4px 12px", borderRadius: 20, border: "none", background: "linear-gradient(135deg,#1D9E75,#0F6E56)", color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>AI ON</button>
                        : <button onClick={() => handleAiToggle(l, true)} style={{ padding: "4px 12px", borderRadius: 20, border: "0.5px solid var(--border2)", background: "transparent", color: "var(--text2)", fontSize: 11, cursor: "pointer" }}>AI OFF</button>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}